import { StateMachine } from "@/core/arch/state_machine";
import type { SupportRequestsRepository } from "./support_repository";
import type {
  SupportTicketRow,
  SupportTicketStatus,
  SupportFilters,
} from "./types";
import { defaultFilters, isClientOnlyPatch } from "./types";

// ── State ─────────────────────────────────────────────────────────────────────

export interface SupportListState {
  /** All filtered items — DataTable handles pagination and sorting. */
  items: SupportTicketRow[];
  filters: SupportFilters;
  status: "idle" | "loading" | "loaded" | "error";
  error?: string;
}

// ── BLoC ──────────────────────────────────────────────────────────────────────

export class SupportListBloc extends StateMachine<SupportListState> {
  private _loadToken = 0;

  /** Full sorted list from the last fetch. */
  private _allItems: SupportTicketRow[] = [];

  constructor(private repo: SupportRequestsRepository) {
    super({
      items: [],
      filters: defaultFilters(),
      status: "idle",
    });
  }

  // ── Public actions ──────────────────────────────────────────────────────────

  async load(): Promise<void> {
    const token = ++this._loadToken;
    this.setState({ ...this.state, status: "loading", error: undefined });

    try {
      const allItems = await this.repo.listAll({ filters: this.state.filters });

      // eslint-disable-next-line security/detect-possible-timing-attacks -- request-sequencing counter, not a secret comparison
      if (token !== this._loadToken) return;

      this._allItems = allItems;
      this.setState({
        ...this.state,
        items: this._applyClientFilters(this._allItems),
        status: "loaded",
        error: undefined,
      });
    } catch (err) {
      // eslint-disable-next-line security/detect-possible-timing-attacks -- request-sequencing counter, not a secret comparison
      if (token !== this._loadToken) return;
      this.setState({
        ...this.state,
        status: "error",
        error:
          err instanceof Error
            ? err.message
            : "Failed to load support requests",
      });
    }
  }

  /**
   * Apply a filter patch. Client-only patches (statuses, topics) re-filter the
   * cached list; server patches (searchQuery) re-fetch.
   */
  async setFilter(patch: Partial<SupportFilters>): Promise<void> {
    this.setState({
      ...this.state,
      filters: { ...this.state.filters, ...patch },
    });

    if (isClientOnlyPatch(patch)) {
      this.setState({
        ...this.state,
        items: this._applyClientFilters(this._allItems),
      });
      return;
    }

    await this.load();
  }

  // ── Private helpers ────────────────────────────────────────────────────────

  private _applyClientFilters(items: SupportTicketRow[]): SupportTicketRow[] {
    const { statuses, topics } = this.state.filters;
    let result = items;

    if (statuses.length > 0) {
      const set = new Set<SupportTicketStatus>(statuses);
      result = result.filter((t) => set.has(t.status));
    }

    if (topics.length > 0) {
      const set = new Set<string>(topics);
      result = result.filter((t) => set.has(t.topic));
    }

    return result;
  }
}
